import type * as React from 'react';

import type {ContextPanelData} from 'common/widgets/context-panel/types';
import type {SnabbdomOrReactNode} from 'common/util/panelish/types';
import {Placement, TooltipAttrs} from 'common/components/tooltip/types';
import type {ModifierKeys} from 'common/util/keyboard';
import {Translatable} from 'common/i18n';
import type {LocalizationStore} from 'common/i18n/store';
import {Directions} from 'common/components/drop-menu/types';

import {SvgIconType} from '../svg-icon/types';
import type {SelectionState} from './constants';

// Marks the element that renders an items menu. Match this rather than the `mp-items-menu` tag name.
export const ITEMS_MENU_ATTR = `data-items-menu`;
export const ITEMS_MENU_SELECTOR = `[${ITEMS_MENU_ATTR}]`;

// Marks the content of an open sub-menu, so styles for a top-level menu can leave the nested menu alone.
export const ITEMS_MENU_SUB_MENU_ATTR = `data-items-menu-sub-menu`;

// The `sourceComponent` an items menu reports in its "Component Added" performance event.
export const ITEMS_MENU_SOURCE_COMPONENT = `mp-items-menu`;

/** Translates a `Translatable` to a string. */
export type PrintTranslatable = LocalizationStore[`print`];

// Marks the option the menu's keyboard and hover focus is on.
export const FOCUSED_ITEM_ATTR = `data-focused`;

export const SELECTED_ITEM_ATTR = `data-selected`;

/**
 * NOTE: Please keep the properties in items menu types generic enough such that they could be used for any list.
 * Not just events or properties. Being generic means we could re-use it in more places and be consistent.
 * We should aim for items-menu as the defacto item list renderer at mixpanel.
 */

export type SectionsFromSearchInputFn = (input: string, currentSections?: Section[]) => Section[];

export type SectionItemContextPanelTransform =
  | ((item: SectionItem) => ContextPanelData)
  | ((item: SectionItem, section: Section) => ContextPanelData);

export type SearchMatchResult = {score: number; matchParts: string[]; matchStr: string};
export type CustomSearchMatcherFn = (item: SectionItem, searchString: string) => SearchMatchResult | null;
export enum LabelTheme {
  Strong = `strong`,
  Soft = `soft`,
  Italic = `italic`,
}
export enum SubLabelTheme {
  Breadcrumb = `breadcrumb`,
}
export enum SelectAllMechanism {
  Full = `full`,
  Partial = `partial`,
}
export enum TagTheme {
  Default = `default`,
  Neutral = `neutral`,
}

/**
 * Too many existing instances of mp-select and mp-items-menu shove additional properties inside SectionItem.
 * To handle these cases we include {[key: string]: any} to allow any extra keys.
 * ExtendWithT has been implemented to slowly correct this overly-loose definition.
 * While abnormal, the approach allows the caller to override the any index signature, therefore making it strict.
 * Ex:
 * MyValueType = {value: string}
 * MyStrictSectionItem = SectionItem<MyValueType, {moreData: string}>;
 * MyStrictSection = Section<MyValueType, SectionItem>;
 *
 * If you do not need to extend SectionItem but still want strict typing use NoExtension;
 * PlainStrictSectionItem = SectionItem<MyValueType, NoExtension>;
 */

// We disable non-nullish values for typing safety.
// However, In this instance we want to use it to signify that there is no values to extends.
// We declare the type here to prevent the need for lint-disables for it's users.
// eslint-disable-next-line @typescript-eslint/no-restricted-types, @typescript-eslint/no-empty-object-type
export type NoExtension = {};

export enum ExpansionToggleLabelMode {
  ShowHide = `show_hide`,
  Unchanged = `unchanged`,
}

export type ExpansionToggle = {
  labelMode: ExpansionToggleLabelMode;
  alwaysShow?: boolean;
  labelSuffix?: {
    icon: string;
    tooltip: TooltipInfo;
  };
} & (
  | // use isExpanded if expansion state should be controlled by the parent
    {isExpanded: boolean}
  // use initialIsExpanded if expansion state should be controlled by this component
  | {initialIsExpanded: boolean}
);

export type Section<T = any, ExtendWithT = Obj, SectionItemT = SectionItem<T, ExtendWithT>> = {
  /** Optional key to identify sections by, useful for event handlers to evaluate which section the event is for */
  key?: string;

  /** Section's label. If not present, only a horizontal separator rule is shown */
  label?: Translatable;

  /** svg-icon shown to the right of the section label */
  icon?: string;

  /** Tooltip shown on icon hover for additional information */
  tooltip?: TooltipInfo;

  /** The items inside the section */
  items?: SectionItemT[];

  /** A spinny is shown inside the section if isLoading is true */
  isLoading?: boolean;

  /** Show the loading spinner even if search text is present */
  loadOnSearch?: boolean;

  /**
   * Only one of showLimit, isHidden, expansionToggle should be used as they're not compatible with each other.
   * */
  /** Number of items to show per section. If more items than showLimit a 'Show All' expander is shown */
  showLimit?: number;
  /** Allows a section to be included in all logic but not rendered in the menu.
      This is useful for conditionally showing after a response from the menuiitem */
  isHidden?: boolean;
  /** If set, the section will render as an expandable/collapsible section. */
  expansionToggle?: ExpansionToggle;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => SnabbdomOrReactNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => SnabbdomOrReactNode;

  /** svg-icon shown to the left of the section label */
  sectionLeftIcon?: string;

  /** Hide the top section divider */
  hideTopDivider?: boolean;

  /** Always show the section label, even if the menu is empty */
  alwaysShowLabel?: boolean;

  /** This is currently used for tags section in the events-menu for rendering a message if the project has no tags.
   * Only available if items are passed via props **/
  emptySectionRenderer?: () => SnabbdomOrReactNode;

  /** Theme for selected item in this section. This overrides the "selected-theme" attr */
  selectedTheme?: SelectedTheme;

  /** Whether the section has is sticky on menu scroll */
  isSticky?: boolean;

  /** Renders an overflow menu at the end of each row, shown on hover */
  overflowMenuRenderer?: (item: SectionItem) => SnabbdomOrReactNode;

  /** Whether to disable selected items in the menu (defaults to true if not specified) */
  disableSelectedItem?: boolean;
};

export type SectionItem<ValueT = any, ExtendWithT = Obj> = {
  /** provides a select handler for each individual item.
   *
   * a boolean return value indicates if selection menus should close on item click or keyboard selection
   */
  onSelect?: (item: SectionItem) => boolean | undefined | null | void | Promise<any>;

  /** Item's label. Pass an empty string for icon-only items */
  label: Translatable;

  /** Append a tag to the end of the label */
  tag?: string;

  /** Theme styling for tag */
  tagTheme?: TagTheme;

  /** A shorter version of label, used in menu summaries */
  shortLabel?: Translatable;

  /** label is truncated by default. wrapLabel: true turns on wrapping so user can read full label */
  wrapLabel?: boolean;

  /** A short label shown on the right side (e.g. for keyboard shortcuts) */
  sideLabel?: string;

  /** A short blurb / description about item that is shown on another line */
  subLabel?: Translatable;

  /** subLabel is truncated by default. wrapSubLabel: true turns on wrapping so user can read full description */
  wrapSubLabel?: boolean;

  /** A badge displayed under the label. */
  subLabelBadge?: string;

  /** Theme styling for the label. */
  labelTheme?: LabelTheme;

  /** Theme styling for the sublabel. */
  subLabelTheme?: SubLabelTheme;

  /** svg-icon shown besides the label */
  icon?: SvgIconType | string;

  /** is the icon highlighted  */
  highlightIcon?: boolean;

  /** By default icon is shown on left, but user can specify right icon e.g BETA icon */
  iconSide?: `left` | `right`;

  /** Can the item be selected. Item is greyed out if disabled */
  isDisabled?: boolean;

  /** Marks the item as selected */
  isSelected?: boolean;

  /** Should '>' caret be shown besides the item. Used to indicate sub items */
  hasCaret?: boolean;

  /** Should '>' caret be shown even when item is not hovered/focused */
  persistCaret?: boolean;

  /** By default, use the caret icon ()>); can specify `arrow` to use the arrow icon (->) */
  caretStyle?: `caret` | `arrow`;

  /** Wrap the item in an anchor tag with a link */
  href?: string | null;

  /** anchor tag target */
  target?: string;

  /** item is not clickable */
  isReadOnly?: boolean;

  /**
   * Also used to indicate sub items, but either item or sub items can be selected.
   * We should re-think this with design. Not the most intuitive UX experience
   */
  hasPill?: boolean;

  /** Label of the pill e.g. 'Properties' or 'Typecast' */
  pillLabel?: string;

  /** Tooltip shown on item hover for additional information */
  tooltip?: TooltipInfo;

  /** Whether the pill is disabled. */
  isPillDisabled?: boolean;

  /** Is this a non-standard menu item (e.g. "show all", "refresh")? It will be rendered differently to
   * visually differentiate it from normal menu items */
  isSpecialAction?: boolean;

  /** Special item that's used to expand a "show all" or "show hidden items" section */
  isExpander?: boolean;

  /** Special item that's used to toggle expand/collapse of the items in a section */
  expansionToggle?: ExpansionToggle;

  /** Hides selectable controls and indicates that this item is not a list item */
  isControlItem?: boolean;

  /** Optional name for the selected value. Equivalent to "value" if not present.
   * Example: "Move to dashboard 333" would be {name: `move`, value: 333}
   **/
  name?: string;

  /** Optional value included in select event */
  value?: ValueT;

  /** Color of the checkbox if it's a checkbox */
  color?: string;

  /** Defines nested sections */
  subSections?: Array<Section<ValueT, ExtendWithT, SectionItem<ValueT>>>;

  /** Shows a search input above the subSections list that filters the nested items by label and sub-label */
  subMenuSearchable?: boolean;

  /** Defines any referenced named slots */
  slot?: string;

  /** Tags whether or not the event was generated by user */
  itemFromInput?: boolean;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => SnabbdomOrReactNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => SnabbdomOrReactNode;

  /** Render a custom sub-menu when the item is focused. Only available if items are passed via props **/
  subMenuRenderer?: (
    onValueSelected: (value: string | number, additionalInfo: Obj) => void,
    positionParentEl: HTMLElement,
  ) => SnabbdomOrReactNode;

  /** Do not consider item when checking for matches against the input **/
  excludeFromInputMatch?: boolean;

  /** Specifies if this property item is in the recently used list */
  isRecentlyUsed?: boolean;

  highlightItem?: boolean;

  /** show regardless if the item matches the search filter */
  ignoreSearchFilter?: boolean;

  /** this item cannot be auto-focused */
  notAutoFocusable?: boolean;
} & ExtendWithT;

export interface TooltipInfo extends Omit<TooltipAttrs, `placement`> {
  /** Placement of the tooltip within the section, only allow left or right */
  placement?: Placement;
}

/** The wrapped section item is for adding internal fields to items that doesn't involve:
 * 1. cloning items (for performance reasons)
 * 2. modifying the passed in items (which breaks isEqual check for determining if menu should re-render if new
 * sections are passed in).
 */
export interface WrappedSectionItem<T = any> {
  index?: number;
  sectionIndex: number;
  labelMatch?: SearchMatchResult | null;
  subLabelMatch?: SearchMatchResult | null;
  customMatch?: SearchMatchResult | null;
  wrapped: SectionItem<T>;
}

/** See comment for WrappedSectionItem for why sections are wrapped */
export interface WrappedSection<T = any> extends Section<T, Obj, WrappedSectionItem> {
  sectionIndex: number;
  items: WrappedSectionItem[];
}

export type WrappedSectionDetails<T = any> = Omit<WrappedSection<T>, `sectionIndex` | `items`>;

export interface ItemsMenuParams {
  onHoverItem?: (item: SectionItem) => void;
  onHoverOut?: () => void;
}

export enum ItemsMenuTheme {
  Default = `default`,
  Compact = `compact`,
  Small = `small`,
  CreateNewMenu = `create-new-menu`,
  VerticalTab = `vertical-tab`,
  GlobalSidenav = `global-sidenav`,
  GlobalSidenavSubmenu = `global-sidenav-submenu`,
  /** Full-height lists of people and boards, such as the home page feeds. */
  Feed = `feed`,
}

export enum SelectedTheme {
  Default = `default`,
  Focus = `focus`,
  Select = `select`,
  Checkbox = `checkbox`,
  Checkmark = `checkmark`,
  LineIndicator = `line-indicator`,
}

export interface ItemsMenuAttrs {
  'align-all-text'?: boolean;
  'context-panel-alignment'?: Directions;
  'disable-first-item-padding'?: boolean;
  'disable-search-filter-matching-parts'?: boolean;
  'disable-search-sort'?: boolean;
  'empty-message'?: string;
  'empty-search-results-message'?: string;
  /** @deprecated use "selected-theme" instead */
  multiple?: boolean;
  'project-is-free-plan'?: boolean;
  'search-exact'?: boolean;
  'search-filter'?: string;
  'select-all-disabled'?: boolean;
  'select-all-mechanism'?: SelectAllMechanism;
  'select-all-tooltip'?: TooltipInfo;
  'selected-theme'?: SelectedTheme;
  'checkbox-icon'?: SvgIconType;
  'show-csv-matches'?: boolean;
  'show-section-label-matches'?: boolean;
  'show-select-all-matching'?: boolean;
  'show-select-all-defined'?: boolean;
  'show-select-all'?: boolean;
  'show-select-all-count'?: boolean;
  'disable-auto-focus'?: boolean;
  'close-sub-menu-on-mouse-leave'?: boolean;
  theme?: ItemsMenuTheme;
  'retain-item-focus'?: boolean;
  'sub-menu-theme'?: ItemsMenuTheme;
}

/** Imperative controls an items menu exposes through its `actionsRef` prop. */
export interface ItemsMenuActions {
  focusItem: (itemIndex: number) => void;
  focusItemWithLabel: (label: string) => void;
  focusItemWithPrefix: (prefix: string) => void;
}

export interface ItemsMenuJsxProps {
  attrs?: ItemsMenuAttrs;
  props?: {
    sectionsFromInputFn?: SectionsFromSearchInputFn;
    contextPanelTransform?: SectionItemContextPanelTransform;
    customSearchMatcherFn?: CustomSearchMatcherFn;
    sections?: Section[];
    keydownProxyEl?: HTMLElement;
    contextPanelPositionParentElement?: HTMLElement;
    actionsRef?: React.RefObject<ItemsMenuActions>;
  };
  params?: ItemsMenuParams;
  on?: {
    navigateBack?: (ev: CustomEvent) => void;
    selectAll?: (ev: CustomEvent) => void;
    selectAllDefined?: (ev: SelectAllDefinedEvent) => void;
    selectAllMatching?: (ev: SelectAllMatchingEvent) => void;
    clickItem?: (ev: ItemClickEvent) => void;
    clickExpanderItem?: (ev: ExpanderItemClickEvent) => void;
    clickExpansionToggleItem?: (ev: ExpansionToggleItemClickEvent) => void;
    clickPill?: (ev: CustomEvent) => void;
    focusItem?: (ev: ItemFocusEvent) => void;
    click?: React.MouseEventHandler<HTMLDivElement>;
    mouseDown?: React.MouseEventHandler<HTMLDivElement>;

    // Composed events from the context panel
    tooltipContextAction?: (ev: CustomEvent) => void;
    tooltipContextEditDescription?: (ev: CustomEvent) => void;
  };
}

export type ExpanderItemClickEvent<T = any> = CustomEvent<{
  section: Section<T>;
  sectionIdx: number;
}>;

export type ExpansionToggleItemClickEvent<T = any> = CustomEvent<{
  section: Section<T>;
  sectionIdx: number;
  isExpanded: boolean;
}>;

export type ItemFocusEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<{
  item: SectionItemT;
  section: SectionT;
}>;

export type ItemClickEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<
  SectionItem<T> & {
    shouldClose: boolean;
    topScrollPosition: number;
    selectionState: SelectionState;
    modifierKeys: ModifierKeys;
    item: SectionItemT;
    section: SectionT;
  }
>;

export type SelectAllDefinedEvent = CustomEvent<{checkSelectAllDefined: boolean}>;

export type SelectAllMatchingEvent<T = any> = CustomEvent<{matchingSections: Array<Section<T>>}>;
